import { PromotionStatsRow } from '../../graphql/promotions'
import { formatMinorWithCurrency } from '../../helpers/promotionAmount'

/**
 * The chart data, built as pure functions rather than inline in the component.
 *
 * Not a style preference — it is the only way the invariants below are checkable. Recharts
 * draws into an SVG sized by ResponsiveContainer, which measures its parent, and every parent
 * is 0x0 under jsdom: nothing inside a chart is ever in the DOM during a test. A test written
 * against the rendered chart can assert that a chart EXISTS and nothing about what it holds.
 *
 * That is not hypothetical. The first version of this lived in the component, and its test —
 * labelled the load-bearing one — asserted that an IRR chart and a USD chart both existed.
 * Deleting the currency filter entirely, so every chart held every currency's rows, left that
 * test green: the containers come from the currency list, not from their contents.
 */

/** Charting every promotion produces an unreadable comb. The table is the complete view. */
export const MAX_BARS = 8

/**
 * A promotion with nothing to draw is not charted.
 *
 * MAX_BARS is a CEILING, never a quota. Taking the top 8 unconditionally filled the remaining
 * slots with zero-valued promotions, so an account with three active promotions drew three bars
 * and five empty rows — the axis labels claiming eight subjects and the plot showing three.
 * hasSomethingToShow does not catch this: it asks whether the chart as a whole is empty, and a
 * chart of 3 real bars and 5 blanks is not.
 *
 * The zero rows are not lost. Every promotion appears in the table below, which is the complete
 * view and the one that answers "did this promotion do anything at all".
 */

/**
 * Two promotions may share a name — seeded and e2e-generated ones routinely do — and a chart
 * keyed on the name draws two identically labelled bars an operator cannot tell apart.
 * Suffixes only the names that actually collide, so the common case reads clean.
 */
function disambiguate<T extends { id: string; name: string }>(
  points: T[]
): T[] {
  const collides = new Set(
    points.map((p) => p.name).filter((n, i, all) => all.indexOf(n) !== i)
  )

  if (collides.size === 0) {
    return points
  }

  // The shortest id tail that ACTUALLY separates them, widening until it does.
  //
  // A fixed four-character slice was the first attempt and is only 16 bits: two ids can share
  // a tail, and because the collision set is keyed on the ORIGINAL name, a still-identical
  // suffixed pair was never re-checked. The reader got two identical labels again, now
  // carrying a suffix implying they had been told apart — worse than not suffixing at all.
  const label = (p: T, tail: number): string =>
    collides.has(p.name)
      ? // "id" spelled out: on a chart whose every other number is a quantity, a bare
        // parenthesised "(1111)" reads as one, and in RTL a parenthesised Latin digit run
        // bidi-flips against a Persian name.
        `${p.name} · id ${tail > 0 ? p.id.slice(-tail) : p.id}`
      : p.name

  const tail = [4, 8, 12, 16, 24].find((n) => {
    const labelled = points.map((p) => label(p, n))
    return new Set(labelled).size === labelled.length
  })

  return points.map((p) => ({ ...p, name: label(p, tail ?? 0) }))
}

export interface UsagePoint {
  /** Kept so two promotions sharing a name can be told apart. */
  id: string
  name: string
  applied: number
  inFlight: number
}

export interface DiscountPoint {
  /** Kept so two promotions sharing a name can be told apart. */
  id: string
  name: string
  /** Major units, for the axis only. */
  discount: number
  /** The original minor string, so a tooltip formats from the source rather than the division. */
  minor: string
  digits: number | null
}

/**
 * Usage spans EVERY promotion regardless of currency.
 *
 * Counts are dimensionless — `formatCount`'s own docstring says so — so they may be compared
 * across currencies. Splitting this per currency would be a different wrong answer.
 */
export function usageSeries(rows: PromotionStatsRow[]): UsagePoint[] {
  return disambiguate(
    rows
      .filter((row) => row.appliedCount > 0 || row.inFlightCount > 0)
      .sort((a, b) => b.appliedCount - a.appliedCount)
      .slice(0, MAX_BARS)
      .map((row) => ({
        id: row.promotionId,
        name: row.name,
        applied: row.appliedCount,
        inFlight: row.inFlightCount
      }))
  )
}

/** The currencies present, so money gets one chart each and never one chart for all. */
export function chartedCurrencies(rows: PromotionStatsRow[]): string[] {
  return Array.from(new Set(rows.map((r) => r.currency))).sort()
}

/**
 * Money for ONE currency, and nothing else.
 *
 * Minor-unit scale differs between currencies, so a series holding both draws 1,000,000 IRR
 * beside 5.00 USD and invites a comparison of bar lengths that mean different things — the
 * defect the money helpers refuse everywhere else, drawn as a picture.
 */
export function discountSeries(
  rows: PromotionStatsRow[],
  currency: string
): DiscountPoint[] {
  return disambiguate(
    rows
      .filter(
        (row) => row.currency === currency && Number(row.discountMinor) > 0
      )
      .sort((a, b) => Number(b.discountMinor) - Number(a.discountMinor))
      .slice(0, MAX_BARS)
      .map((row) => ({
        id: row.promotionId,
        name: row.name,
        discount: Number(row.discountMinor) / 10 ** (row.minorUnitDigits ?? 0),
        minor: row.discountMinor,
        digits: row.minorUnitDigits
      }))
  )
}

/**
 * An all-zero chart draws axes and no data; the totals panel already reported the zero.
 *
 * FINITE, not merely non-zero. `discountMinor` is a string off the wire, so a malformed one
 * divides to NaN — and `NaN !== 0` is TRUE, which let a chart of garbage through the very
 * check meant to suppress an empty one. A bar cannot be drawn at NaN, so the axis renders and
 * the plot does not: the same nothing the zero case already refuses, arrived at differently.
 */
export function hasSomethingToShow(series: DiscountPoint[]): boolean {
  return series.some(
    (point) => Number.isFinite(point.discount) && point.discount !== 0
  )
}

/**
 * The money tooltip's text.
 *
 * Exported, and named, because a formatter written inline in JSX is unreachable by any test
 * here: Recharts never mounts the SVG tree that would invoke it, so the callback that decides
 * what an operator reads off a hover could not be exercised at all.
 *
 * Formats from the ORIGINAL minor string, not from the major-unit number the bar is drawn at.
 * That division exists to give the axis a readable scale; routing the tooltip through it too
 * would show a figure derived twice, and this is the number an operator acts on.
 */
export function discountTooltipText(
  point: Pick<DiscountPoint, 'minor' | 'digits'> | undefined,
  currency: string
): string {
  return formatMinorWithCurrency(point?.minor ?? '0', currency, point?.digits)
}

/**
 * Whether the in-flight series has anything to draw.
 *
 * Lives here, not in the component, for the reason at the top of this file: a legend key
 * rendered for a series with no bars is invisible to every test, because Recharts draws
 * nothing under jsdom. Written inline in the JSX it was untestable — a mutation that showed
 * the legend unconditionally failed nothing.
 */
export function hasInFlight(series: UsagePoint[]): boolean {
  return series.some((point) => point.inFlight > 0)
}
